import { useAuth } from '@/components/auth/AuthContext';
import React, { useState, useCallback, useMemo } from "react";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Toaster } from "@/components/ui/toaster";
import { InventoryView } from "@/components/inventory/InventoryView";
import { InventoryFilter } from "@/components/inventory/InventoryFilter";
import { useInventory } from "@/hooks/useInventory";
import { clearInventoryCache } from "@/lib/api/inventory";
import { ManufacturingExpansionProvider } from "@/contexts/ManufacturingExpansionContext";
import { ManufacturingViewContextProvider } from "@/contexts/ManufacturingViewContext";

export function Inventory() {
  const { user } = useAuth();
  const STOCK_PENDING_FILTERS = {
    RELEVANT: "relevant",
    STOCK_ONLY: "stock_only",
    PENDING_ONLY: "pending_only",
    PENDING_WITH_STOCK: "pending_with_stock",
    PENDING_WITHOUT_STOCK: "pending_without_stock"
  };

  const [viewMode, setViewMode] = useState(() => {
    const stored = localStorage.getItem("inventoryViewMode");
    return stored || "summary";
  });
  const [stockPendingFilterMode, setStockPendingFilterMode] = useState(() => {
    const storedMode = localStorage.getItem("inventoryStockPendingFilterMode");
    if (storedMode) return storedMode;
    // Compatibilidad hacia atrás con el filtro booleano anterior
    return STOCK_PENDING_FILTERS.RELEVANT;
  });
  const [filter, setFilter] = useState(null);
  const [seasonFilter, setSeasonFilter] = useState(() => {
    return localStorage.getItem("inventorySeasonFilter") || "all";
  });

  const {
    inventory,
    products,
    orders,
    loading,
    recalculating,
    progress,
    stats,
    error,
    recalculate,
    refresh
  } = useInventory();

  const { toast } = useToast();

  const availableSeasons = useMemo(() => {
    return [...new Set(
      (products || [])
        .map((product) => product.temporada?.trim?.() || product.temporada)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b, "es"));
  }, [products]);

  const seasonFilteredProducts = useMemo(() => {
    if (seasonFilter === "all") return products;
    return products.filter((product) => product.temporada === seasonFilter);
  }, [products, seasonFilter]);

  const seasonFilteredProductIds = useMemo(() => {
    return new Set(seasonFilteredProducts.map((product) => product.id));
  }, [seasonFilteredProducts]);

  const seasonFilteredInventory = useMemo(() => {
    return inventory.filter((item) => seasonFilteredProductIds.has(item.product_id));
  }, [inventory, seasonFilteredProductIds]);

  const seasonFilteredProductsNeedingManufacturing = useMemo(() => {
    return seasonFilteredProducts.filter((product) => {
      const productInventory = seasonFilteredInventory.filter((item) => item.product_id === product.id);
      const hasPendingOrders = productInventory.some((item) => (Number(item.pending_orders) || 0) > 0);
      const hasSpecificShortages = productInventory.some((item) => {
        const stock = Number(item.quantity) || 0;
        const pending = Number(item.pending_orders) || 0;
        return pending > stock;
      });

      return hasPendingOrders && hasSpecificShortages;
    });
  }, [seasonFilteredProducts, seasonFilteredInventory]);

  const seasonStats = useMemo(() => {
    const totals = seasonFilteredInventory.reduce((acc, item) => {
      const quantity = Number(item.quantity) || 0;
      const pending = Number(item.pending_orders) || 0;
      acc.totalStock += quantity;
      acc.totalPending += pending;
      acc.totalUsable += Math.min(quantity, pending);
      acc.totalFree += Math.max(quantity - pending, 0);
      return acc;
    }, { totalStock: 0, totalPending: 0, totalUsable: 0, totalFree: 0, totalValue: 0 });

    const priceById = new Map(seasonFilteredProducts.map((product) => [product.id, Number(product.price) || 0]));
    totals.totalValue = seasonFilteredInventory.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      return sum + (quantity * (priceById.get(item.product_id) || 0));
    }, 0);

    return totals;
  }, [seasonFilteredInventory, seasonFilteredProducts]);

  const matchesStockPendingFilter = useCallback((stock, pending, mode) => {
    const stockValue = Number(stock) || 0;
    const pendingValue = Number(pending) || 0;

    switch (mode) {
      case STOCK_PENDING_FILTERS.STOCK_ONLY:
        return stockValue > 0;
      case STOCK_PENDING_FILTERS.PENDING_ONLY:
        return pendingValue > 0;
      case STOCK_PENDING_FILTERS.PENDING_WITH_STOCK:
        return pendingValue > 0 && stockValue > 0;
      case STOCK_PENDING_FILTERS.PENDING_WITHOUT_STOCK:
        return pendingValue > 0 && stockValue <= 0;
      case STOCK_PENDING_FILTERS.RELEVANT:
      default:
        return stockValue > 0 || pendingValue > 0;
    }
  }, []);

  const filteredInventory = useMemo(() => {
    return seasonFilteredInventory.filter((item) =>
      matchesStockPendingFilter(item.quantity, item.pending_orders, stockPendingFilterMode)
    );
  }, [seasonFilteredInventory, matchesStockPendingFilter, stockPendingFilterMode]);

  const handleReset = useCallback(async () => {
    try {
      toast({
        title: "🔄 Iniciando Recálculo Optimizado",
        description: "El proceso será más rápido que antes...",
      });

      const result = await recalculate();
      
      toast({
        title: "🎉 ¡Completado!",
        description: `Reconciliación completada. ${result.processedItems} combinaciones actualizadas.`,
      });
    } catch (error) {
      toast({
        title: "❌ Error General",
        description: "Ha ocurrido un error durante el recálculo: " + error.message,
        variant: "destructive"
      });
    }
  }, [recalculate, toast]);

  const handleRefresh = useCallback(async () => {
    try {
      // Limpiar caché antes de refrescar
      clearInventoryCache();
      await refresh();
      toast({
        title: "✅ Datos Actualizados",
        description: "El inventario ha sido refrescado",
      });
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "Error al refrescar los datos: " + error.message,
        variant: "destructive"
      });
    }
  }, [refresh, toast]);

  // Mostrar error si existe
  if (error) {
    return (
      <div className="space-y-6">
        <Toaster />
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-red-600 font-medium">Error:</div>
            <div className="ml-2 text-red-700">{error}</div>
          </div>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            className="mt-2"
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toaster />
      
      {/* Barra de progreso para recálculo */}
      {recalculating && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-800">
              {progress.message}
            </span>
            <span className="text-sm text-blue-600">
              {progress.percentage}%
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.percentage}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Estadísticas rápidas */}
      {stats && (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Filtro global por temporada</h3>
              <p className="text-sm text-gray-500">
                Afecta a indicadores, listados y pendiente de fabricación.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="min-w-[220px]">
                <Select
                  value={seasonFilter}
                  onValueChange={(value) => {
                    setSeasonFilter(value);
                    localStorage.setItem("inventorySeasonFilter", value);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todas las temporadas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las temporadas</SelectItem>
                    {availableSeasons.map((season) => (
                      <SelectItem key={season} value={season}>
                        {season}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {seasonFilter !== "all" && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSeasonFilter("all");
                    localStorage.setItem("inventorySeasonFilter", "all");
                  }}
                >
                  Limpiar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {stats && (
        <TooltipProvider>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-green-600">{seasonStats.totalStock}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              Unidades en almacén
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>Suma de quantity (entradas − salidas) por producto/talla/color</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-blue-600">{seasonStats.totalPending}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              Pedidos Pendientes
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>Suma de pending_orders (desde las líneas de pedido con pendiente)</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-teal-600">{seasonStats.totalUsable}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              Unid. Utilizables (para servir)
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>Suma de min(quantity, pending_orders). Máximo que puede servirse inmediatamente</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-amber-600">{seasonStats.totalFree}</div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              Unid. Libres (sin pedido)
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>Suma de max(quantity − pending_orders, 0). Stock no comprometido</TooltipContent>
              </Tooltip>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-2xl font-bold text-purple-600">
              {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(seasonStats.totalValue)}
            </div>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              Valor Total
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-gray-400 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>Suma de quantity × precio del producto</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
        </TooltipProvider>
      )}

      {/* Filtro de búsqueda */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-medium mb-3">Filtro de Búsqueda</h3>
        <InventoryFilter
          key={seasonFilter}
          onFilterChange={setFilter}
          products={seasonFilteredProducts}
          globalSeason={seasonFilter}
        />
      </div>

      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={viewMode === "detailed" ? "default" : "outline"}
            onClick={() => {
              setViewMode("detailed");
              localStorage.setItem("inventoryViewMode", "detailed");
            }}
          >
            Vista Detallada
          </Button>
          <Button
            variant={viewMode === "summary" ? "default" : "outline"}
            onClick={() => {
              setViewMode("summary");
              localStorage.setItem("inventoryViewMode", "summary");
            }}
          >
            Vista Resumida
          </Button>
          <Button
            variant={viewMode === "manufacturing" ? "default" : "outline"}
            onClick={() => {
              setViewMode("manufacturing");
              localStorage.setItem("inventoryViewMode", "manufacturing");
            }}
          >
            Pendiente Fabricación
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || recalculating}
          >
            🔄 Refrescar
          </Button>
          <label className="flex items-center gap-2 text-sm">
            Estado:
            <select
              value={stockPendingFilterMode}
              onChange={(e) => {
                const value = e.target.value;
                setStockPendingFilterMode(value);
                localStorage.setItem("inventoryStockPendingFilterMode", value);
              }}
              className="border rounded px-2 py-1 bg-white text-sm"
            >
              <option value={STOCK_PENDING_FILTERS.RELEVANT}>Con stock o pedidos pendientes</option>
              <option value={STOCK_PENDING_FILTERS.STOCK_ONLY}>Solo con stock</option>
              <option value={STOCK_PENDING_FILTERS.PENDING_ONLY}>Con pedidos pendientes</option>
              <option value={STOCK_PENDING_FILTERS.PENDING_WITH_STOCK}>Pedidos pendientes y stock</option>
              <option value={STOCK_PENDING_FILTERS.PENDING_WITHOUT_STOCK}>Pedidos pendientes sin stock</option>
            </select>
          </label>
          <Button
            variant="destructive"
            onClick={handleReset}
            disabled={loading || recalculating || user?.role !== 'admin'}
          >
            {recalculating ? "Recalculando..." : "Recalcular Inventario"}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          Cargando inventario...
        </div>
      ) : (
        <ManufacturingExpansionProvider>
          <ManufacturingViewContextProvider>
            <InventoryView
              inventory={seasonFilteredInventory}
              filteredInventory={filteredInventory}
              products={seasonFilteredProducts}
              orders={orders}
              viewMode={viewMode}
              stockPendingFilterMode={stockPendingFilterMode}
              productsNeedingManufacturing={seasonFilteredProductsNeedingManufacturing}
              filter={filter}
            />
          </ManufacturingViewContextProvider>
        </ManufacturingExpansionProvider>
      )}
    </div>
  );
}
